import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db917as6oi3g3tv8urlg";
const FORM_ID = "db917as6oi3g3tv8urlg";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  useSeo({
    title: "联系我们 - 欲望的诱惑",
    description:
      "通过欲望的诱惑的联系页面，提交你的内容建议、资料纠错或合作意向，我们会认真查看每一条反馈。",
    keywords: "联系我们,联系方式,反馈建议,欲望的诱惑,影视资讯站",
    canonicalPath: "/contact",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessageLen(0);
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被识别为异常，请修改后重试。"
            : typeof serverMsg === "string" && serverMsg
              ? serverMsg
              : "提交失败，请稍后重试。"
        );
      }
    } catch (error) {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  const contactItems = [
    { icon: "ri-mail-line", label: "邮箱", value: "hello@yuwangdeyouhuo.com" },
    { icon: "ri-phone-line", label: "电话", value: "400-820-1234" },
    { icon: "ri-map-pin-line", label: "地址", value: "上海市静安区影视创意园 A 座 12 层" },
    { icon: "ri-time-line", label: "服务时间", value: "周一至周五 9:30 - 18:30" },
  ];

  const socials = [
    { icon: "ri-weibo-fill", label: "官方微博" },
    { icon: "ri-wechat-fill", label: "微信公众号" },
    { icon: "ri-music-2-fill", label: "视频号" },
    { icon: "ri-movie-2-fill", label: "影迷社群" },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          title="联系我们"
          description="有任何建议、资料补充或合作意向，欢迎通过以下方式与我们取得联系。"
          crumbs={[{ label: "首页", path: "/" }, { label: "联系我们" }]}
        />

        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">联系方式</h2>
              <p className="text-sm text-foreground-600 mb-6">
                我们通常在 1-2 个工作日内回复你的邮件与留言。
              </p>

              <div className="space-y-3">
                {contactItems.map((item) => (
                  <div key={item.label} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <span className="w-10 h-10 shrink-0 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-0.5 text-sm text-foreground-900 break-words">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="font-heading text-base font-semibold text-foreground-950 mt-8 mb-3">关注我们</h3>
              <div className="grid grid-cols-2 gap-3">
                {socials.map((s) => (
                  <div key={s.label} className="flex items-center gap-2.5 p-3 rounded-lg bg-background-100 border border-background-200/70">
                    <span className="w-9 h-9 rounded-full bg-background-50 flex items-center justify-center text-primary-600">
                      <i className={`${s.icon} text-base`}></i>
                    </span>
                    <span className="text-sm text-foreground-700">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">在线反馈</h2>
                <p className="text-sm text-foreground-600 mb-6">
                  填写下方表单告诉我们你的想法，我们会认真阅读每一条反馈。
                </p>

                {status === "success" ? (
                  <div className="text-center py-10">
                    <span className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-3xl text-primary-600"></i>
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">提交成功，感谢你的反馈！</h3>
                    <p className="mt-1.5 text-sm text-foreground-500">我们已收到你的留言，会尽快查看并处理。</p>
                    <button
                      onClick={() => setStatus("idle")}
                      className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 border border-background-300/70 text-sm font-medium hover:text-primary-600 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line"></i>
                      再写一条
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form={FORM_ID} noValidate={false}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-2">
                          你的称呼 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          maxLength={40}
                          placeholder="请输入你的称呼"
                          className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-2">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="用于接收我们的回复"
                          className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-2">
                        反馈类型 <span className="text-primary-500">*</span>
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        required
                        defaultValue=""
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="" disabled>请选择反馈类型</option>
                        <option value="内容建议">内容建议</option>
                        <option value="资料纠错">资料纠错</option>
                        <option value="合作意向">合作意向</option>
                        <option value="其他问题">其他问题</option>
                      </select>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <span className="text-xs text-foreground-400">{messageLen}/500</span>
                      </div>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        onChange={(e) => setMessageLen(e.target.value.length)}
                        placeholder="请详细描述你的建议或问题（最多 500 字）"
                        className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none leading-relaxed"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field-7x"
                    />

                    {status === "error" && formError && (
                      <p className="mt-4 text-sm text-primary-600 flex items-center gap-2">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中…
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill"></i>
                          提交反馈
                        </>
                      )}
                    </button>

                    <p className="mt-3 text-xs text-foreground-400">
                      提交即表示你同意我们为处理反馈而使用你提供的联系方式，我们不会用于其他用途。
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}